import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRoleService } from '../user-services/user-roles.service';
import { UserRole } from '../../models/user-roles.class';
import { BaseCrudService } from '../form-submittion-services/base-crud.service';
import { ProductionReportsService } from '../components-reusable-services/production-reports.service';

@Injectable({
  providedIn: 'root',
})
export class InboxCountService {
  private _totalUnapproved = new BehaviorSubject<number>(0);
  totalUnapproved$ = this._totalUnapproved.asObservable();
  private loaded = false;
  private loading = false;
  private lastRoleKey: string | null = null;
  constructor(
    private pdr: ProductionReportsService,
    private userRoleService: UserRoleService,
    private baseCRUD: BaseCrudService<any>,
  ) {}

  private lastRoleId: number | null = null;
  private inFlightPromise: Promise<void> | null = null;

  async fetchUnapprovedCount(role: UserRole, force = false): Promise<void> {
    const roleId = role?.roleId;

    // 1. Skip if already loaded for same role
    if (!force && this.loaded && this.lastRoleId === roleId) {
      return;
    }

    // 2. If a request is already running → reuse it
    if (this.inFlightPromise) {
      return this.inFlightPromise;
    }

    this.inFlightPromise = (async () => {
      this.loading = true;

      try {
        const roleKey = this.getRoleKey();
        const categoryIds = JSON.parse(roleKey);

        if (!categoryIds.length) {
          this._totalUnapproved.next(0);
          this.loaded = true;
          this.lastRoleId = roleId;
          return;
        }

        const total = await this.fetchUnapprovedCountsByCategories(categoryIds);

        this._totalUnapproved.next(total);

        // Cache result
        this.loaded = true;
        this.lastRoleId = roleId;
      } catch (err) {
        console.error('Failed to fetch unapproved count:', err);
      } finally {
        this.loading = false;
        this.inFlightPromise = null;
      }
    })();

    return this.inFlightPromise;
  }
  updateRecord(add: boolean): void {
    const current = this._totalUnapproved.getValue();
    const next = add ? current + 1 : current - 1;

    this._totalUnapproved.next(Math.max(0, next));
  }

  get currentUnapproved(): number {
    return this._totalUnapproved.getValue();
  }
  private refreshTimeout: any;

  refreshUnapproved(force = false): void {
    clearTimeout(this.refreshTimeout);
    const role = this.userRoleService.getEffectiveRole();
    this.refreshTimeout = setTimeout(() => {
      this.fetchUnapprovedCount(role, force);
    }, 300);
  }
  private getRoleKey(): string {
    const approvedIds = this.userRoleService.getApprovalDepts();
    return JSON.stringify(approvedIds);
  }
  async fetchUnapprovedCountsByCategories(
    categoryIds: number[],
  ): Promise<number> {
    if (!categoryIds?.length) {
      return 0;
    }

    try {
      const results = await this.fetchAllUnapproved(categoryIds);

      return results.reduce(
        (total, records) => total + this.filterVisibleRecords(records).length,
        0,
      );
    } catch (err) {
      console.error('Error calculating unapproved counts:', err);
      return 0;
    }
  }

  async fetchAllUnapproved(categoryIds: number[]): Promise<any[][]> {
    const fetchPromises: Promise<any[]>[] = [];

    if (categoryIds.includes(1)) {
      const millService = await this.pdr.getService('MillRun');

      if (millService?.getRecordsNotApproved) {
        fetchPromises.push(millService.getRecordsNotApproved());
      }
    }

    if (categoryIds.includes(2)) {
      const kilnService = await this.pdr.getService('KilnReport');

      if (kilnService?.getRecordsNotApproved) {
        fetchPromises.push(kilnService.getRecordsNotApproved());
      }
    }

    if (categoryIds.includes(3)) {
      const pkgService = await this.pdr.getService('PkgReport');

      if (pkgService?.getRecordsNotApproved) {
        fetchPromises.push(pkgService.getRecordsNotApproved());
      }
    }

    if (categoryIds.includes(12)) {
      const shapeService = await this.pdr.getService('DailyShapes');

      if (shapeService?.getRecordsNotApproved) {
        fetchPromises.push(shapeService.getRecordsNotApproved());
      }
    }

    return Promise.all(fetchPromises);
  }
  private filterVisibleRecords(records: any[]): any[] {
    const role = this.userRoleService.getEffectiveRole();

    if (!role) {
      return [];
    }

    const locations = role.location ?? [];
    const plants = role.plant ?? [];
    const shifts = role.shift ?? [];

    return records.filter((record) => {
      const locationVisible = locations.includes(Number(record.location));
      const plantVisible = plants.includes(Number(record.plant));
      const shiftVisible = record.shiftId
        ? shifts.includes(Number(record.shiftId))
        : true;

      return locationVisible && plantVisible && shiftVisible;
    });
  }
}
